import { FiltersMap } from '@wix/bex-core';
import { useEffect, useMemo, useState } from 'react';
import { action, reaction, runInAction } from 'mobx';
import { ToolbarCollectionProps } from './ToolbarCollection';
import { checkConflictingToolbarProps } from './checkConflictingToolbarProps';
import { useCollectionPageContext } from '../../providers';

export function useToolbarCollectionSyncProps<T, F extends FiltersMap>(
  props: ToolbarCollectionProps<T, F> & {
    isUsingDragAndDrop: boolean;
    isUsingBulkActionsToolbar: boolean;
    isUsingHorizontalScroll: boolean;
    isUsingFolders: boolean;
    isUsingTableVirtualization: boolean;
  },
) {
  const {
    state,
    views,
    tabs,
    exportButton,
    extensionMenuItems,
    dragAndDropCategories,
    maxSelection,
    search,
    addNewButton,
    filters,
    isUsingDragAndDrop,
    isUsingBulkActionsToolbar,
    isUsingHorizontalScroll,
    isUsingFolders,
    isUsingTableVirtualization,
    summaryBar,
  } = props;

  checkConflictingToolbarProps(props);

  state.internalScroll = props.internalScroll;

  const pageState = useCollectionPageContext();

  const getIsUsingAllItemsView = () =>
    !!(
      // views=undefined, it means thw user didn't pass so allItemsViews=false
      (
        views &&
        // views.props=undefined, it means the user passed views without configuring allItems so allItemsViews=true
        (!views.props ||
          // views.props.allItemsViewProps=undefined, it means the user passed allItemsViewProps
          // without configuring the hidden so allItemsViews=true
          !views.props.allItemsViewProps ||
          // allItemsViews depends on hidden's value
          !views.props.allItemsViewProps.hidden)
      )
    );

  useEffect(() => {
    if (exportButton) {
      state.featuredComponents.exportButton.set(exportButton);
    }

    return () => {
      state.featuredComponents.exportButton.set(null);
    };
  }, [exportButton]);

  const tabsFilterProps = useMemo(() => {
    return tabs?.props
      ? {
          filter: tabs.props.filter,
        }
      : undefined;
  }, [tabs]);

  useEffect(
    () =>
      reaction(
        () => pageState.hasHeader,
        (hasHeader) => {
          if (hasHeader) {
            state.minimizedHeaderHeight = 66;
          }
        },
        {
          fireImmediately: true,
        },
      ),
    [],
  );

  useEffect(() => {
    runInAction(() => {
      state.dragAndDropCategories = dragAndDropCategories;
    });
    // We accept this prop once on purpose because it's array it changes on every render. We expose a setter `set dragAndDropCategories` instead.
  }, []);

  useState(() => views?.type.setViewsState(state, views?.props));

  useEffect(
    action(() => {
      state.viewsProps = views?.props;
    }),
    [views],
  );

  useState(() => {
    props.FiltersPanelApplyButtonBehavior?.setStateObject(state);

    state.extensionMenuItems = extensionMenuItems;
    state.featuresAvailability = {
      isDisablingSearch: search === false,
      isUsingFilters: !!filters,
      isUsingCreateNewInCollection: !!addNewButton,
      isUsingDragAndDrop,
      isUsingBulkActionsToolbar,
      isUsingHorizontalScroll,
      isUsingFolders,
      isUsingTableVirtualization,
      isUsingTags: !!props.tags,
      isUsingTabs: !!tabs,
      isUsingSummaryRow: !!summaryBar,
    };

    state.additionalFeaturesAvailability = {
      isUsingTableSubtitle: !!props.subtitle,
      isUsingCounterInHeader: !!props.showTotal,
      isUsingSelectAllLimitation: !!props.maxSelection,
      isUsingErrorMessage: !!props.renderError,
      isUsingAllItemsView: getIsUsingAllItemsView(),
      isUsingCustomEmptyState: !!props.renderEmptyState,
      isUsingPrimaryActionCta: !!props.primaryActionButton,
    };
  });

  useEffect(
    action(() => {
      if (tabsFilterProps?.filter) {
        state.tabsFilterProps = tabsFilterProps;
        tabsFilterProps.filter.persistent = true;
      }
    }),
    [tabsFilterProps],
  );

  useEffect(() => {
    runInAction(() => {
      state.bulk.maxSelection = maxSelection;
    });
  }, [maxSelection]);

  return state;
}
